import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { MdCloud, MdComputer, MdExpandMore } from 'react-icons/md';
import useOnClickOutside from '@hooks/useOnClickOutside';
import type { LocalRunner } from '@api/localRunners';
import {
  RunnerPickerContainer,
  RunnerPickerTrigger,
  RunnerPickerMenu,
  RunnerPickerItem,
} from './Chat.styled';

interface RunnerPickerProps {
  runnerType: string;
  localRunners: readonly Pick<LocalRunner, 'vmId' | 'name' | 'status'>[];
  onPick: (value: string) => void;
  disabled?: boolean;
  menuDirection?: 'up' | 'down';
  /** False when the work can only run on one of the user's own runners. */
  allowCloud?: boolean;
}

const RunnerPicker: React.FC<RunnerPickerProps> = ({
  runnerType,
  localRunners,
  onPick,
  disabled,
  menuDirection = 'up',
  allowCloud = true,
}) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const ref = useOnClickOutside<HTMLDivElement>(() => setOpen(false));

  const getRunnerStatusLabel = (status: string) => {
    if (status === 'READY') return intl.formatMessage({ id: 'chat.runner.status.online', defaultMessage: 'Online' });
    if (status === 'ALLOCATED') return intl.formatMessage({ id: 'chat.runner.status.busy', defaultMessage: 'Busy' });
    return intl.formatMessage({ id: 'chat.runner.status.offline', defaultMessage: 'Offline' });
  };

  const getLabel = () => {
    if (runnerType === 'cloud') {
      return intl.formatMessage({ id: 'chat.runner.cloud', defaultMessage: 'Cloud Runner' });
    }
    const runner = localRunners.find((r) => r.vmId === runnerType);
    if (runner) {
      return `${runner.name} (${getRunnerStatusLabel(runner.status)})`;
    }
    return allowCloud
      ? intl.formatMessage({ id: 'chat.runner.cloud', defaultMessage: 'Cloud Runner' })
      : intl.formatMessage({ id: 'chat.runner.choose', defaultMessage: 'Choose a runner' });
  };

  const handlePick = (value: string) => {
    onPick(value);
    setOpen(false);
  };

  return (
    <RunnerPickerContainer ref={ref}>
      <RunnerPickerTrigger
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        data-testid="runner-picker"
      >
        {runnerType === 'cloud' ? <MdCloud /> : <MdComputer />}
        {getLabel()}
        <MdExpandMore />
      </RunnerPickerTrigger>
      {open && (
        <RunnerPickerMenu $menuDirection={menuDirection}>
          {allowCloud && (
            <RunnerPickerItem
              $selected={runnerType === 'cloud'}
              onClick={() => handlePick('cloud')}
            >
              <MdCloud />
              {intl.formatMessage({ id: 'chat.runner.cloud', defaultMessage: 'Cloud Runner' })}
            </RunnerPickerItem>
          )}
          {localRunners.map((r) => {
            const isAvailable = r.status === 'READY' || r.status === 'ALLOCATED';
            return (
            <RunnerPickerItem
              key={r.vmId}
              $selected={isAvailable && runnerType === r.vmId}
              $disabled={!isAvailable}
              aria-disabled={!isAvailable}
              onClick={() => isAvailable && handlePick(r.vmId)}
            >
              <MdComputer />
              {r.name} ({getRunnerStatusLabel(r.status)})
            </RunnerPickerItem>
            );
          })}
          {localRunners.length === 0 && (
            <RunnerPickerItem $selected={false} $disabled>
              <MdComputer />
              {intl.formatMessage({ id: 'chat.runner.noLocal', defaultMessage: 'No local runners' })}
            </RunnerPickerItem>
          )}
        </RunnerPickerMenu>
      )}
    </RunnerPickerContainer>
  );
};

export default RunnerPicker;
